import { useState, useEffect } from "react";
import { Search, Filter } from "lucide-react";
import axios from "axios";
import { StudyCard, StudyCardSkeleton } from "../components/StudyCard";
import { StudyDetailsModal } from "../components/StudyDetailsModal";
import { usePageMeta } from "../hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Top-level "I'm a Caregiver" page , shows live caregiver studies.
 */
export default function CaregiverPage() {
  const [studies, setStudies] = useState([]);
  const [filteredStudies, setFilteredStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [selectedStudy, setSelectedStudy] = useState(null);

  usePageMeta({
    path: "/caregiver",
    title:
      "Caregiver research studies , Paid healthcare studies for caregivers · MediTalk",
    description:
      "Live healthcare research studies looking for caregivers. Share what life caring for a loved one is really like in surveys, interviews and focus groups , and get paid for your time.",
  });

  useEffect(() => {
    fetchStudies();
  }, []);

  useEffect(() => {
    filterStudies();
  }, [searchTerm, countryFilter, studies]);

  const fetchStudies = async () => {
    try {
      const response = await axios.get(
        `${API}/studies?study_type=caregiver&active_only=true`,
      );
      setStudies(response.data);
      setFilteredStudies(response.data);
    } catch (error) {
      console.error("Error fetching studies:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterStudies = () => {
    let filtered = [...studies];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (study) =>
          study.title.toLowerCase().includes(term) ||
          study.condition.toLowerCase().includes(term) ||
          study.description.toLowerCase().includes(term),
      );
    }
    if (countryFilter) {
      filtered = filtered.filter((study) =>
        study.country.toLowerCase().includes(countryFilter.toLowerCase()),
      );
    }
    setFilteredStudies(filtered);
  };

  const handleRegister = (study) => {
    setSelectedStudy(study);
  };

  const countries = [
    ...new Set(
      studies.flatMap((s) => s.country.split(" and ").map((c) => c.trim())),
    ),
  ];

  return (
    <div className="min-h-screen">
      {/* Hero - DARK */}
      <section className="bg-[var(--dark-bg)] grain-dark py-10 md:py-14 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-[var(--lime)]/10 blur-3xl pointer-events-none" />
        <div className="container-main relative z-10">
          <div className="max-w-3xl">
            <span className="badge badge-sage mb-3">For Caregivers</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-['Outfit'] font-bold text-white leading-[1.05] mb-4">
              Studies looking for
              <span className="text-[var(--lime)]"> caregivers like you.</span>
            </h1>
            <p className="text-lg text-[var(--gray-light)] leading-relaxed max-w-2xl">
              Caring for someone with a condition gives you invaluable
              perspective. Browse current opportunities across a range of
              formats including interviews, surveys, discussion boards,
              diaries and more.
            </p>
          </div>
        </div>
      </section>

      {/* Filters - LIGHT */}
      <section className="py-4 md:py-5 bg-white border-b border-[var(--light-border)] sticky top-20 z-30">
        <div className="container-main">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search
                size={20}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]"
              />
              <input
                type="text"
                placeholder="Search by condition or keyword…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full h-12 pl-12 pr-4 rounded-xl border border-[var(--light-border)] bg-[var(--light-card)] text-[var(--navy)] focus:border-[var(--teal)] focus:ring-2 focus:ring-[var(--teal)]/20 transition-all"
                data-testid="search-input"
              />
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <label
                htmlFor="country-filter"
                className="text-sm font-medium text-[var(--text-muted)] whitespace-nowrap"
              >
                Filter on:
              </label>
              <div className="relative">
                <Filter
                  size={20}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]"
                />
                <select
                  id="country-filter"
                  value={countryFilter}
                  onChange={(e) => setCountryFilter(e.target.value)}
                  className="h-12 pl-12 pr-8 rounded-xl border border-[var(--light-border)] bg-[var(--light-card)] text-[var(--navy)] focus:border-[var(--teal)] focus:ring-2 focus:ring-[var(--teal)]/20 transition-all appearance-none min-w-[180px]"
                  data-testid="country-filter"
                >
                  <option value="">All countries</option>
                  {countries.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Studies Grid - LIGHT */}
      <section className="py-6 md:py-8 bg-[var(--light-card)]">
        <div className="container-main">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <StudyCardSkeleton key={i} variant="light" />
              ))}
            </div>
          ) : filteredStudies.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredStudies.map((study) => (
                <StudyCard
                  key={study.id}
                  study={study}
                  onRegister={handleRegister}
                  variant="light"
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-[var(--text-muted)] mb-4">
                No caregiver studies right now, please check back soon.
              </p>
              <button
                onClick={() => {
                  setSearchTerm("");
                  setCountryFilter("");
                }}
                className="btn-secondary-light"
                data-testid="clear-filters-btn"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      </section>

      {selectedStudy && (
        <StudyDetailsModal
          study={selectedStudy}
          audience="caregiver"
          onClose={() => setSelectedStudy(null)}
        />
      )}
    </div>
  );
}
